/**
 * The page's side of the bridge to the Finpilot Notetaker app (#9163): what
 * `react-native-webview` puts on the page it hosts. The app reads every
 * message in its `src/web/shell.ts` (`parseShellMessage`).
 */

/** What `react-native-webview` puts on the page it hosts. */
interface ReactNativeWebViewBridge {
  postMessage(data: string): void;
}

/** The app's bridge, or null outside the app's web view. */
function webViewBridge(): ReactNativeWebViewBridge | null {
  const bridge = (window as { ReactNativeWebView?: ReactNativeWebViewBridge })
    .ReactNativeWebView;
  return bridge && typeof bridge.postMessage === "function" ? bridge : null;
}

/** Sends `message` to the app as JSON; nothing outside the app's web view. */
export function postToApp(message: { type: string }): void {
  webViewBridge()?.postMessage(JSON.stringify(message));
}

/** Asks the app to start taking notes: on a calendar meeting (a schedule
 *  row's tap), or on none (the home's "Take notes", #15177). */
export const START_RECORDING_MESSAGE_TYPE = "fp:start-recording";

/** Asks the app to open its own Settings screen (the home menu's Settings,
 *  #15177): the app's account, notifications and the meetings waiting to
 *  upload live there, not in the web view. */
export const OPEN_SETTINGS_MESSAGE_TYPE = "fp:open-settings";

export interface OpenSettingsMessage {
  type: typeof OPEN_SETTINGS_MESSAGE_TYPE;
}

/**
 * The meeting the app takes notes on, in the wire shape of the calendar
 * read; the app creates its server meeting from these fields as the desktop
 * app does. `timezone` is the event's stated zone, else the viewer's. With
 * no `meeting`, the app starts notes on a meeting of its own.
 */
export interface StartRecordingMessage {
  type: typeof START_RECORDING_MESSAGE_TYPE;
  meeting?: {
    event_id: string;
    title: string;
    meeting_url: string | null;
    attendees: string[];
    attendee_names: string[];
    start_time: string;
    end_time: string;
    timezone: string | null;
  };
}

/**
 * Asks the app for the phone's own share sheet with this link (#15177, the
 * meeting page's "Share via…"): the app owns the OS chrome, so the page hands
 * it the link and the title.
 */
export const SHARE_MESSAGE_TYPE = "fp:share";

export interface ShareMessage {
  type: typeof SHARE_MESSAGE_TYPE;
  url: string;
  title: string;
}
